import { Link, useParams } from "react-router-dom";
import GetDesignFolders from "../Tools/GetDesignFolders";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../../App";
import fetchWithRetries from "../../Tools/FetchWithRetries";
import GetSizeModels from "../Tools/GetSizeModels";
import GetCycloneByID from "../Tools/GetCycloneByID";
import GetAirflowsByDesignFolderID from "../Tools/GetAirflowsByDesignFolderID";
import toast from "react-hot-toast";
import CreateUpdateCyclones from "../Tools/CreateUpdateCyclones";

const CyclonesDetail = () => {

    const { id } = useParams();
    const [user] = useContext(UserContext);

    const [cyclone, setCyclone] = useState(null);
    const [designFolders, setDesignFolders] = useState([]);
    const [sizeModels, setSizeModels] = useState([]);
    const [cmAirOutletCapSizeModels, setCmAirOutletCapSizeModels] = useState([]);
    const [drawThroughOutletSizeModels, setDrawThroughOutletSizeModels] = useState([]);
    const [rainCapSizeModels, setRainCapSizeModels] = useState([]);

    const designFolderSelectRef = useRef(null);

    // UI States
    const [isImporting, setIsImporting] = useState(false);
    const [showReadyRow, setShowReadyRow] = useState(false);

    // Because it is annoying to invoke these strings multiple times
    const DAF_KEYS = ["daf1", "daf2", "daf3", "daf4", "daf5", "daf6", "daf7", "daf8", "daf9", "daf10"];
    const DAF_NOTE_KEYS = ["daf1Notes", "daf2Notes", "daf3Notes", "daf4Notes", "daf5Notes", "daf6Notes", "daf7Notes", "daf8Notes", "daf9Notes", "daf10Notes"];

    // Helpers
    const updateCyclone = useCallback((patch) => {
        setCyclone(prev => ({ ...prev, ...patch }))
        //console.log("Updated Cyclone:", { ...cyclone, ...patch });
    }, []);

    const safeNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const formatDate = (dateString) => {
        if (!dateString || typeof dateString !== "string") return "";
        const parts = dateString.split("/");
        if (parts.length !== 3) return "";
        const month = String(parts[0] ?? "").padStart(2, "0");
        const day = String(parts[1] ?? "").padStart(2, "0");
        const year = String(parts[2] ?? "");
        return `${year}-${month}-${day}`;
    };

    const unformatDate = (isoString) => {
        if (!isoString || typeof isoString !== "string") return "?";
        const [year, month, day] = isoString.split("-");
        if (!year || !month || !day) return "?";
        return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
    };

    // When the user deletes airflow values, compact the list to remove gaps
    const compactDafs = useCallback((pairs) => {
        const nonZero = pairs.filter(p => Number(p.airflow) > 0);
        const zeros = pairs.filter(p => !(Number(p.airflow) > 0));
        const compacted = [...nonZero, ...zeros].slice(0, DAF_KEYS.length);
        while (compacted.length < DAF_KEYS.length) compacted.push({ airflow: "0", note: "" });
        return compacted;
    }, []);

    // Calculated Values
    const dafValues = useMemo(() => DAF_KEYS.map(k => safeNum(cyclone?.[k])), [cyclone]);

    const dafSubTotal = useMemo(() => {
        const sum = dafValues.reduce((acc, n) => acc + (n > 0 ? n : 0), 0);
        return Number(sum.toFixed(4));
    }, [dafValues]);

    const dafTotal = useMemo(() => Number((dafSubTotal * 1.1).toFixed(4)), [dafSubTotal]);

    //  Dynamically draw the number of visible airflow rows based on filled values
    const dafPairs = useMemo(() => DAF_KEYS.map((k, i) => ({ key: k, noteKey: DAF_NOTE_KEYS[i], airflow: String(cyclone?.[k] ?? "0"), note: String(cyclone?.[DAF_NOTE_KEYS[i]] ?? "") })), [cyclone]);

    const readyIndex = useMemo(() => {
        const idx = dafPairs.findIndex(p => Number(p.airflow) <= 0);
        return idx == -1 ? DAF_KEYS.length : idx; // if full, no ready slot
    }, [dafPairs]);

    const filteredSizeModels = useMemo(() => {
        const t = String(cyclone?.cycloneTypeSelection || "");
        if (!t) return [];
        return sizeModels
            .filter(sm => String(sm.Type) == t)
            .sort((a, b) => Number(a.Size) - Number(b.Size));
    }, [sizeModels, cyclone?.cycloneTypeSelection]);

    const sizeModelsWithActualSP = useMemo(() => {
        const adjustedTotalAirflow = safeNum(dafTotal);
        const hasGuard = String(cyclone?.screenedTubularGuard || "0") == "1";
        const capType = String(cyclone?.capType || "1");
        const desiredSP = safeNum(cyclone?.desiredOperatingPressureLoss);

        const rows = filteredSizeModels.map(sm => {

            const sizeModelCFM = safeNum(sm.CFM);
            if (sizeModelCFM <= 0 || adjustedTotalAirflow <= 0 || desiredSP <= 0) return { ...sm, ActualSP: "", DifferenceSP: "", Recommended: false };

            // 1) Divide [adjusted total airflow cfm] by [size model cfm] to get flow ratio
            let flowRatio = adjustedTotalAirflow / sizeModelCFM;

            // 2) Square root the flow ratio and then multiply by 2 because cyclone cfm is measured at 2 in. H2O
            let actualSP = 2 * (Math.sqrt(flowRatio));

            // 3) If there is a screened tubular guard, divide by 2 
            if (hasGuard) {
                actualSP /= 2;
            }

            // 4) Add other feature adjustments
            if (capType == "2") { // C-M Air Outlet
                actualSP += 0.25;
            }
            else if (capType == "3") { // Draw Through Outlet
                actualSP += 0.10;
            }
            else if (capType == "4") { // Rain Cap
                actualSP += 0.05;
            }

            // Difference SP = desired minus actual (positive means we are under desired, negative means we are over desired)
            let differenceSP = desiredSP - actualSP;

            // 5) Assign the "Actual SP" value and the "Difference SP" value
            return { ...sm, ActualSP: Number(actualSP.toFixed(4)), DifferenceSP: Number(differenceSP.toFixed(4)), Recommended: false };

        });

        // Recommended = closest DifferenceSP to 0 without going positive (i.e., DifferenceSP <= 0 and maximal)
        const eligible = rows.filter(r => r.DifferenceSP !== "" && Number.isFinite(Number(r.DifferenceSP)) && Number(r.DifferenceSP) <= 0);
        const bestId = eligible.length ? eligible.reduce((best, cur) => (cur.DifferenceSP > best.DifferenceSP ? cur : best), eligible[0]).id : null;

        return rows.map(r => ({ ...r, Recommended: bestId != null && r.id == bestId }));

    }, [filteredSizeModels, cyclone?.screenedTubularGuard, cyclone?.capType, cyclone?.desiredOperatingPressureLoss, dafTotal]);

    const recommendedSizeModel = useMemo(() => sizeModelsWithActualSP.find(x => x.Recommended) || null, [sizeModelsWithActualSP]);

    const selectedSizeModel = useMemo(() => {
        const selectedId = cyclone?.sizeModelID;
        if (!selectedId) return null;
        return sizeModelsWithActualSP.find(x => String(x.id) == String(selectedId)) || null;
    }, [sizeModelsWithActualSP, cyclone?.sizeModelID]);

    const isSelectedSameAsRecommended = useMemo(() => {
        if (!selectedSizeModel || !recommendedSizeModel) return null;
        return String(selectedSizeModel.id) == String(recommendedSizeModel.id);
    }, [selectedSizeModel, recommendedSizeModel]);

    const selectedCapMeta = useMemo(() => {
        const capType = String(cyclone?.capType || "1");
        if (capType == "1") return { capType, label: "No Cap", img: null, list: [] };

        if (capType == "2") return { capType, label: "C-M Air Outlet Cap", img: "/img/Cyclones/CMAirOutletCap.png", list: cmAirOutletCapSizeModels || [] };
        if (capType == "3") return { capType, label: "Draw Through Outlet Cap", img: "/img/Cyclones/DrawThroughOutlet.png", list: drawThroughOutletSizeModels || [] };
        return { capType, label: "Rain Cap", img: "/img/Cyclones/ChineseTypeRainCap.png", list: rainCapSizeModels || [] };
    }, [cyclone?.capType, cmAirOutletCapSizeModels, drawThroughOutletSizeModels, rainCapSizeModels]);

    const selectedCapModel = useMemo(() => {
        const size = selectedSizeModel?.Size;
        if (!size || !selectedCapMeta?.list?.length) return null;

        // Support either { Size: "12" } style or array-style entries if your JSON is not normalized
        const byObjSize = selectedCapMeta.list.find(x => String(x?.Size) == String(size));
        if (byObjSize) return byObjSize;

        // Fallback: if entries are like [size, ...] or { size: ... }
        const byAlt = selectedCapMeta.list.find(x => String(x?.size) == String(size) || String(x?.[0]) == String(size));
        return byAlt || null;
    }, [selectedCapMeta, selectedSizeModel?.Size]);

    const capSpecsRows = useMemo(() => {
        if (String(cyclone?.capType || "1") == "1") return [];

        if (!selectedSizeModel?.Size) return [["Status", "Select a cyclone size model first"]];
        if (!selectedCapMeta?.list?.length) return [["Status", "Cap size list not loaded"]];
        if (!selectedCapModel) return [["Status", `No ${selectedCapMeta.label} specs found for size ${selectedSizeModel.Size}`]];

        // If your JSON is objects, this auto-lists all keys except common ones
        if (selectedCapModel && !Array.isArray(selectedCapModel)) {
            const hidden = new Set(["id", "ID", "Size", "size", "Type", "type", "Name", "name"]);
            return Object.keys(selectedCapModel)
                .filter(k => !hidden.has(k))
                .map(k => [k, selectedCapModel[k]]);
        }

        // If your JSON is arrays, you'll want to map indices to labels here
        return [["Specs", JSON.stringify(selectedCapModel)]];
    }, [cyclone?.capType, selectedCapMeta, selectedCapModel, selectedSizeModel?.Size]);


    const handleSelectSizeModel = useCallback((sm) => {
        if (!sm?.id) return;
        updateCyclone({
            sizeModelID: sm.id,
            sizeModelSize: sm.Size,
            sizeModelType: sm.Type,
            sizeModelCFM: sm.CFM,
            sizeModelActualSP: sm.ActualSP == "" ? "" : sm.ActualSP,
            sizeModelDifferenceSP: sm.DifferenceSP == "" ? "" : sm.DifferenceSP,
            sizeModelWasRecommendedAtSelection: !!sm.Recommended,

            sizeModelMaterialGauge: sm.MaterialGauge,
            sizeModelCollectorWeight: sm.CollectorWeight,
            sizeModelCMOutletWeight: sm.CMOutletWeight,
            sizeModelStandWeight: sm.StandWeight,

            sizeModelDimension_AA: sm.Dimension_AA,
            sizeModelDimension_A: sm.Dimension_A,
            sizeModelDimension_B: sm.Dimension_B,
            sizeModelDimension_C: sm.Dimension_C,
            sizeModelDimension_D: sm.Dimension_D,
            sizeModelDimension_E: sm.Dimension_E,
            sizeModelDimension_G: sm.Dimension_G,
            sizeModelDimension_H: sm.Dimension_H,
            sizeModelDimension_K: sm.Dimension_K,
            sizeModelDimension_L: sm.Dimension_L,
            sizeModelDimension_M: sm.Dimension_M,
            sizeModelDimension_P: sm.Dimension_P,
            sizeModelDimension_Q_a: sm.Dimension_Q_a,
            sizeModelDimension_Q_b: sm.Dimension_Q_b,
            sizeModelDimension_Q_d: sm.Dimension_Q_d,
            sizeModelDimension_R: sm.Dimension_R,
            sizeModelDimension_S_Length: sm.Dimension_S_Length,
            sizeModelDimension_S_Pcs: sm.Dimension_S_Pcs,
            sizeModelDimension_S_Flange: sm.Dimension_S_Flange,
            sizeModelDimension_S_Gauge: sm.Dimension_S_Gauge,
            sizeModelDimension_T: sm.Dimension_T,
            sizeModelDimension_X: sm.Dimension_X,
            sizeModelDimension_Y: sm.Dimension_Y,
            sizeModelDimension_Z: sm.Dimension_Z,
            sizeModelDimension_ZZ: sm.Dimension_ZZ
        });
    }, [updateCyclone]);

    // Get Cyclone By ID
    useEffect(() => {
        if (!id) return;
        GetCycloneByID(id).then((response) => {
            if (!response?.success) return;
            const row = JSON.parse(response.payload)?.data?.[0];
            if (!row) return;
            setCyclone(
                {
                    cycloneID: row[0],
                    cycloneName: row[1],
                    customer: row[2],
                    quoteNumber: row[3],
                    cycloneTypeSelection: row[4],
                    sizeModelID: row[5],
                    sizeModelSize: row[6],
                    sizeModelType: row[7],
                    sizeModelCFM: row[8],
                    capType: row[9],
                    capTypeName: row[10],
                    screenedTubularGuard: row[11] == "True" ? "1" : "0",
                    desiredOperatingPressureLoss: row[12],
                    daf1: row[13], daf2: row[14], daf3: row[15], daf4: row[16], daf5: row[17], daf6: row[18], daf7: row[19], daf8: row[20], daf9: row[21], daf10: row[22],
                    daf1Notes: row[23], daf2Notes: row[24], daf3Notes: row[25], daf4Notes: row[26], daf5Notes: row[27], daf6Notes: row[28], daf7Notes: row[29], daf8Notes: row[30], daf9Notes: row[31], daf10Notes: row[32],
                    notes: row[33],
                    dateEntered: formatDate(row[34]),
                    dateModified: formatDate(row[35]),
                    createdBy: row[36],
                    createdByFirstName: row[37],
                    createdByLastName: row[38],
                    modifiedBy: row[39],
                    modifiedByFirstName: row[40],
                    modifiedByLastName: row[41]
                }
            );
        });
    }, [id]);

    useEffect(() => {
        if (!cyclone) return;

        // Wait until sizeModelsWithActualSP is actually computed/populated
        if (!Array.isArray(sizeModelsWithActualSP) || sizeModelsWithActualSP.length == 0) return;

        const currentSelectedId = cyclone?.sizeModelID;

        const selectedStillValid =
            String(currentSelectedId || "") !== "" &&
            sizeModelsWithActualSP.some(x => String(x.id) == String(currentSelectedId));

        // If we already have a saved selection and it's valid, keep it.
        if (selectedStillValid) return;

        // If we have a saved selection but it's not valid anymore (type changed / removed), fall back to recommended.
        if (String(currentSelectedId || "") !== "") {
            if (recommendedSizeModel?.id) handleSelectSizeModel(recommendedSizeModel);
            else updateCyclone({ sizeModelID: "" });
            return;
        }

        // If nothing is selected at all, auto-select recommended.
        if (recommendedSizeModel?.id) handleSelectSizeModel(recommendedSizeModel);

    }, [cyclone?.sizeModelID, sizeModelsWithActualSP, recommendedSizeModel?.id, handleSelectSizeModel, updateCyclone]);

    // Get Design Folders
    useEffect(() => {
        fetchWithRetries(3, GetDesignFolders, (payload) => {
            const data = JSON.parse(payload)?.data || [];
            data.sort((a, b) => new Date(b[5]) - new Date(a[5]));
            const list = data.map(df => ({
                DesignFolderId: df[0],
                DesignNum: "DP" + (df[1]?.toString().padStart(6, "0") || "000000"),
                DesignedBy: df[2],
                Email: df[3],
                Notes: df[4],
                CreatedDate: df[5],
                ModifiedDate: df[6],
                ReferenceNum: df[7],
                CustomerReferenceNum: df[8],
                MaterialDetails: df[9],
                Customer: df[10],
                TotalDesignAirflow: df[11],
                NumberOfAirflows: df[12],
            }));
            setDesignFolders(list);
        });
    }, []);

    // Get Size Models
    useEffect(() => {
        fetchWithRetries(3, GetSizeModels, (payload) => {
            const data = JSON.parse(payload)?.data || [];
            const list = data.map(sm => ({
                id: sm[0],
                Type: sm[1],
                Size: sm[2],
                CFM: sm[3],
                MaterialGauge: sm[4],
                CollectorWeight: sm[5],
                CMOutletWeight: sm[6],
                StandWeight: sm[7],
                Dimension_AA: sm[8],
                Dimension_A: sm[9],
                Dimension_B: sm[10],
                Dimension_C: sm[11],
                Dimension_D: sm[12],
                Dimension_E: sm[13],
                Dimension_G: sm[14],
                Dimension_H: sm[15],
                Dimension_K: sm[16],
                Dimension_L: sm[17],
                Dimension_M: sm[18],
                Dimension_P: sm[19],
                Dimension_Q_a: sm[20],
                Dimension_Q_b: sm[21],
                Dimension_Q_d: sm[22],
                Dimension_R: sm[23],
                Dimension_S_Length: sm[24],
                Dimension_S_Pcs: sm[25],
                Dimension_S_Flange: sm[26],
                Dimension_S_Gauge: sm[27],
                Dimension_T: sm[28],
                Dimension_X: sm[29],
                Dimension_Y: sm[30],
                Dimension_Z: sm[31],
                Dimension_ZZ: sm[32]
            }));
            setSizeModels(list);
        });

    }, []);

    // Get CM Air Outlet Cap Size Models, Draw Through Outlet Size Models, Rain Cap Size Models
    useEffect(() => {
        fetch("/data/cmairoutlets.json").then(r => r.json()).then(setCmAirOutletCapSizeModels).catch(() => { });
        fetch("/data/drawthroughoutlets.json").then(r => r.json()).then(setDrawThroughOutletSizeModels).catch(() => { });
        fetch("/data/raincaps.json").then(r => r.json()).then(setRainCapSizeModels).catch(() => { });
    }, []);

    // Import Design Airflow Values
    const handleImport = useCallback((designFolderId, selectionText) => {
        if (!designFolderId) return;

        if (designFolderSelectRef.current) designFolderSelectRef.current.value = "";

        GetAirflowsByDesignFolderID(designFolderId).then((response) => {
            if (!response?.success) return;

            const data = JSON.parse(response.payload)?.data || [];
            const airflows = data.map(af => ({ DesignNum: af[0], Notes: af[1], Airflow: String(parseFloat(af[2] || 0).toFixed(0)) }));

            const dafPatch = {};
            DAF_KEYS.forEach((k, i) => { dafPatch[k] = airflows[i]?.Airflow ?? "0"; });
            DAF_NOTE_KEYS.forEach((k) => { dafPatch[k] = ""; });

            updateCyclone(dafPatch);

            toast.success(<><span>Imported successfully</span></>, { duration: 4000 });
            setIsImporting(false);
        });
    }, [updateCyclone]);

    const handleSave = useCallback(() => {

        if (!selectedSizeModel?.id) { toast.error(<>Select a size model first.</>, { duration: 4000 }); return; }

        CreateUpdateCyclones(
            cyclone.cycloneID,
            cyclone.cycloneName,
            cyclone.customer,
            cyclone.quoteNumber,
            cyclone.cycloneTypeSelection,
            selectedSizeModel?.id || "",
            cyclone.capType || "1",
            cyclone.screenedTubularGuard || "0",
            cyclone.desiredOperatingPressureLoss || 0,
            safeNum(cyclone.daf1), safeNum(cyclone.daf2), safeNum(cyclone.daf3), safeNum(cyclone.daf4), safeNum(cyclone.daf5),
            safeNum(cyclone.daf6), safeNum(cyclone.daf7), safeNum(cyclone.daf8), safeNum(cyclone.daf9), safeNum(cyclone.daf10),
            cyclone.daf1Notes || "", cyclone.daf2Notes || "", cyclone.daf3Notes || "", cyclone.daf4Notes || "", cyclone.daf5Notes || "",
            cyclone.daf6Notes || "", cyclone.daf7Notes || "", cyclone.daf8Notes || "", cyclone.daf9Notes || "", cyclone.daf10Notes || "",
            cyclone.notes ?? "",
            cyclone.createdBy,
            user?.id
        )
            .then((response) => {
                if (response?.success) toast.success(<>{`Cyclone ${cyclone.cycloneName} saved successfully!`}</>, { duration: 4000 });
                else toast.error(<>{`Error saving Cyclone ${cyclone.cycloneName}: ${response?.message || "Unknown error"}`}</>, { duration: 4000 });
            })
            .catch((error) => toast.error(<>{`Error saving Cyclone ${cyclone.cycloneName}: ${error?.message || "Unknown error"}`}</>, { duration: 4000 }));
    }, [selectedSizeModel, cyclone, user?.id]);

    return (
        <>

            <style>{`
            @media print {

                /* make it a little tighter */
                @page { size: letter; margin: 0.35in; }

                /* hide non-print UI */
                .no-print, .no-print * { display: none !important; }

                body { zoom: 0.6; }

                /* if zoom is ignored (some browsers), this helps prevent overflow */
                html, body { overflow: visible !important; }

                /* keep colors */
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            `}</style>


            <div className="print-host" style={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", margin: "32px 0px" }}>
                <div className="print-root" style={{ width: "1200px", display: "flex", flexDirection: "column", gap: "0px" }}>

                    <button className="btn btn-success no-print" style={{ width: "128px", position: "fixed", bottom: "16px", right: "16px", zIndex: "99" }} onClick={handleSave}>Save</button>

                    {/* Return to Cyclones */}
                    <div className="no-print" style={{ width: "1200px", display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: "16px" }}>
                        <Link to="/Cyclones" className="btn btn-default">Back to Cyclones</Link>
                        <button className="btn btn-default" onClick={window.print}>Print</button>
                    </div>

                    {/* Details */}
                    <div className="card" style={{ width: "1200px" }}>
                        <div className="card-header" style={{ backgroundColor: "#158cba", color: "var(--bs-light)" }}>
                            <span style={{ fontSize: "24px" }}>{cyclone?.cycloneName}</span>
                        </div>

                        <div className="card-body">

                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around", margin: "1%", width: "98%" }}>
                                <div style={{ width: "100%", marginTop: "8px", color: "var(--bs-secondary)" }}>
                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end" }}>
                                        <i>Created By {cyclone?.createdByFirstName} {cyclone?.createdByLastName} on {cyclone?.dateEntered ? unformatDate(cyclone.dateEntered) : "?"}</i>
                                        <i>Last Modified By {cyclone?.modifiedByFirstName} {cyclone?.modifiedByLastName} on {cyclone?.dateModified ? unformatDate(cyclone.dateModified) : "?"}</i>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around", margin: "1%", width: "98%" }}>
                                <div style={{ width: "100%", marginTop: "8px" }}>
                                    <label htmlFor="cycloneName">Cyclone Name</label>
                                    <input type="text" autoComplete="off" className="form-control" id="cycloneName" value={cyclone?.cycloneName ?? ""} style={{ width: "100%", maxWidth: "100%" }} onChange={(e) => updateCyclone({ cycloneName: e.target.value })} />
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around", margin: "1%", width: "98%" }}>
                                <div style={{ width: "50%", marginTop: "8px" }}>
                                    <label htmlFor="customer">Customer</label>
                                    <input type="text" autoComplete="off" className="form-control" style={{ maxWidth: "98%", margin: "1%" }} id="customer" value={cyclone?.customer ?? ""} onChange={(e) => updateCyclone({ customer: e.target.value })} />
                                </div>

                                <div style={{ width: "50%", marginTop: "8px" }}>
                                    <label htmlFor="quoteNumber">Quote Number</label>
                                    <input type="text" autoComplete="off" className="form-control" style={{ maxWidth: "98%", margin: "1%" }} id="quoteNumber" value={cyclone?.quoteNumber ?? ""} onChange={(e) => updateCyclone({ quoteNumber: e.target.value })} />
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around", margin: "1%", width: "98%" }}>
                                <div style={{ width: "100%", marginTop: "8px" }}>
                                    <label htmlFor="notes">Notes</label>
                                    <textarea className="form-control" id="notes" rows="5" value={cyclone?.notes ?? ""} style={{ width: "100%", maxWidth: "100%" }} autoComplete="off" onChange={(e) => updateCyclone({ notes: e.target.value })}></textarea>
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around", margin: "1%", width: "98%" }}>
                                <div style={{ width: "50%", marginTop: "8px" }}>
                                    <label htmlFor="cycloneTypeSelection">Cyclone Type</label>
                                    <select className="form-select" id="cycloneTypeSelection" style={{ fontSize: "16px", height: "38px", maxWidth: "98%", margin: "1%" }} value={cyclone?.cycloneTypeSelection ?? ""} onChange={(e) => updateCyclone({ cycloneTypeSelection: e.target.value })}>
                                        <option value="">Select Type</option>
                                        <option value="C7">C7 (Circular Discharge)</option>
                                        <option value="P1">P1 (Rectangular Discharge)</option>
                                    </select>
                                </div>

                                <div style={{ width: "50%", marginTop: "8px" }}>
                                    <label htmlFor="desiredOperatingPressureLoss">Desired Operating Pressure Loss<span style={{ color: "var(--bs-secondary)" }}>&nbsp;(in. H₂0)</span></label>
                                    <select className="form-select" id="desiredOperatingPressureLoss" style={{ fontSize: "16px", height: "38px", maxWidth: "98%", margin: "1%" }} value={cyclone?.desiredOperatingPressureLoss ?? ""} onChange={(e) => updateCyclone({ desiredOperatingPressureLoss: e.target.value })}>
                                        <option value="">Select Value</option>
                                        {["1.0", "1.5", "2.0", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0", "5.5", "6.0", "6.5", "7.0"].map(v => <option key={v} value={v}>{v}{v == "2.0" ? " (standard)" : ""}</option>)}
                                    </select>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Features */}
                    <div className="card" style={{ width: "1200px", marginTop: "32px" }}>
                        <div className="card-header" style={{ backgroundColor: "#158cba", color: "var(--bs-light)" }}>
                            <span style={{ fontSize: "24px" }}>Features</span>
                        </div>

                        <div className="card-body">

                            <div className="form-check form-switch no-print" style={{ margin: "24px 0px 24px 8px", width: "50%", display: "flex", alignItems: "center", gap: "12px" }}>
                                <input className="form-check-input" type="checkbox" role="switch" id="screenedTubularGuard"
                                    checked={String(cyclone?.screenedTubularGuard) == "1"}
                                    onChange={(e) => updateCyclone({ screenedTubularGuard: e.target.checked ? "1" : "0" })}
                                    style={{ transform: "scale(1.35)", marginTop: "0" }}
                                />
                                <label className="form-check-label" htmlFor="screenedTubularGuard" style={{ fontSize: "16px", marginBottom: "0" }}>
                                    Screened Tubular Guard
                                    <span style={{ color: "var(--bs-secondary)", fontWeight: "bold" }}>
                                        &nbsp;(Impacts Total Pressure Loss before other features)
                                    </span>
                                </label>
                            </div>

                            {/* PRINT VERSION */}
                            <div className="print-only" style={{ margin: "16px 0" }}>
                                <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                                    Screened Tubular Guard:
                                </span>
                                <span style={{ marginLeft: "8px", fontSize: "16px" }}>
                                    {String(cyclone?.screenedTubularGuard) == "1" ? "YES" : "NO"}
                                </span>
                            </div>


                            <div style={{ display: "flex", flexDirection: "row", gap: "16px", marginTop: "16px" }}>

                                {/* LEFT: vertical cap selectors */}
                                <div style={{ width: "50%", display: "flex", flexDirection: "column", gap: "10px" }}>

                                    <input type="radio" className="btn-check" name="outletCap" id="noCap" autoComplete="off" checked={String(cyclone?.capType) == "1"} onChange={() => updateCyclone({ capType: "1" })} />
                                    <label className={`btn btn-light ${String(cyclone?.capType) == "1" ? "" : "no-print"}`} htmlFor="noCap" style={{ fontSize: "18px", border: "2px solid var(--bs-dark)", borderRadius: "8px", textAlign: "left", padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                                        <strong>No Cap</strong>
                                    </label>

                                    <input type="radio" className="btn-check" name="outletCap" id="cmCap" autoComplete="off" checked={String(cyclone?.capType) == "2"} onChange={() => updateCyclone({ capType: "2" })} />
                                    <label className={`btn btn-light ${String(cyclone?.capType) == "2" ? "" : "no-print"}`} htmlFor="cmCap" style={{ fontSize: "18px", border: "2px solid var(--bs-dark)", borderRadius: "8px", textAlign: "left", padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                                        <strong>C-M Air Outlet</strong>
                                        <span style={{ color: "var(--bs-secondary)", fontWeight: "bold", fontSize: "15px" }}>+ 0.25 in. H₂O</span>
                                    </label>

                                    <input type="radio" className="btn-check" name="outletCap" id="drawCap" autoComplete="off" checked={String(cyclone?.capType) == "3"} onChange={() => updateCyclone({ capType: "3" })} />
                                    <label className={`btn btn-light ${String(cyclone?.capType) == "3" ? "" : "no-print"}`} htmlFor="drawCap" style={{ fontSize: "18px", border: "2px solid var(--bs-dark)", borderRadius: "8px", textAlign: "left", padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                                        <strong>Draw Through Outlet</strong>
                                        <span style={{ color: "var(--bs-secondary)", fontWeight: "bold", fontSize: "15px" }}>+ 0.10 in. H₂O</span>
                                    </label>

                                    <input type="radio" className="btn-check" name="outletCap" id="rainCap" autoComplete="off" checked={String(cyclone?.capType) == "4"} onChange={() => updateCyclone({ capType: "4" })} />
                                    <label className={`btn btn-light ${String(cyclone?.capType) == "4" ? "" : "no-print"}`} htmlFor="rainCap" style={{ fontSize: "18px", border: "2px solid var(--bs-dark)", borderRadius: "8px", textAlign: "left", padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                                        <strong>Rain Cap</strong>
                                        <span style={{ color: "var(--bs-secondary)", fontWeight: "bold", fontSize: "15px" }}>+ 0.05 in. H₂O</span>
                                    </label>

                                </div>

                                {/* RIGHT: image stage (reserved space + normalized sizes) */}
                                <div style={{ flex: 1, border: "2px solid var(--bs-secondary)", borderRadius: "8px", padding: "12px", backgroundColor: "var(--bs-light)", display: "flex", flexDirection: "column", gap: "10px" }}>

                                    <div style={{ fontWeight: "bold", color: "#158cba" }}>
                                        {String(cyclone?.capType) == "1" ? "No Cap" : String(cyclone?.capType) == "2" ? "C-M Air Outlet Cap" : String(cyclone?.capType) == "3" ? "Draw Through Outlet Cap" : "Rain Cap"}
                                    </div>

                                    <div style={{ width: "100%", height: "240px", display: "flex", justifyContent: "center", alignItems: "center", border: "1px dashed var(--bs-secondary)", borderRadius: "8px", backgroundColor: "var(--bs-white)" }}>
                                        {String(cyclone?.capType) == "1" ? (
                                            <i style={{ color: "var(--bs-secondary)" }}>No schematic for this selection</i>
                                        ) : (
                                            <img
                                                src={
                                                    String(cyclone?.capType) == "2" ? "/img/Cyclones/CMAirOutletCap.png"
                                                        : String(cyclone?.capType) == "3" ? "/img/Cyclones/DrawThroughOutlet.png"
                                                            : "/img/Cyclones/ChineseTypeRainCap.png"
                                                }
                                                alt="Schematic"
                                                style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                                            />
                                        )}
                                    </div>

                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Airflow Values */}
                    <div className="card" style={{ width: "1200px", marginTop: "32px" }}>
                        <div className="card-header" style={{ backgroundColor: "#158cba", color: "var(--bs-light)" }}>
                            <span style={{ fontSize: "24px" }}>Airflow Values (CFM)</span>
                        </div>

                        <div className="card-body">
                            {!isImporting
                                ? (
                                    <>
                                        <button className="btn btn-secondary no-print" style={{ margin: "1%" }} onClick={() => setIsImporting(prev => !prev)}>Import Airflows</button>
                                    </>
                                )
                                : (
                                    <>

                                        <div className="no-print" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", margin: "1%", width: "98%", border: "1px solid var(--bs-secondary)", padding: "8px", borderRadius: "6px", backgroundColor: "var(--bs-gray-500)" }}>

                                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", marginTop: "4px", marginRight: "16px" }}>
                                                <span style={{ color: "var(--bs-light)", marginLeft: "8px" }}>Select a Design Folder to import Airflow values</span>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", width: "100%" }}>
                                                <select ref={designFolderSelectRef} className="form-select" id="designAirflow" style={{ fontSize: "16px", height: "38px", marginTop: "8px", maxWidth: "100%" }} onChange={(e) => handleImport(e.target.value, e.target.options[e.target.selectedIndex].text)}>
                                                    <option value="">Select Design Folder</option>
                                                    {designFolders.map((df) => (
                                                        <option key={df.DesignFolderId} value={df.DesignFolderId}>
                                                            {df.DesignNum}&nbsp;&nbsp;&nbsp;&nbsp; {df.Customer} by {df.DesignedBy} on {df.CreatedDate} &nbsp;&nbsp;&nbsp;&nbsp; {df.NumberOfAirflows} Branch{df.NumberOfAirflows > 1 ? "es" : ""} &nbsp;&nbsp;&nbsp;&nbsp; {Number(df.TotalDesignAirflow).toLocaleString("en-US")} Total CFM
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginTop: "4px" }}>
                                                <span style={{ color: "var(--bs-light)", marginLeft: "8px" }}><b>Warning: </b><i>All current Airflow Values and Notes will be overwritten</i></span>
                                                <button className="btn btn-sm btn-danger no-print" style={{ marginTop: "4px" }} onClick={() => setIsImporting(prev => !prev)}>Cancel Import</button>
                                            </div>
                                        </div>
                                    </>
                                )
                            }

                            {dafPairs.map((p, i) => {
                                if (Number(p.airflow) <= 0 && !(showReadyRow && i == readyIndex)) return null;

                                const isReadyRow = showReadyRow && i == readyIndex;

                                return (
                                    <div key={p.key} style={{ display: "flex", flexDirection: "row", justifyContent: "center", margin: "0% 1%", width: "98%", backgroundColor: isReadyRow ? "var(--bs-white)" : "var(--bs-gray-200)", alignItems: "center" }}>
                                        {isReadyRow ? (
                                            <button className="btn btn-outline-secondary" title="Cancel" style={{ height: "38px", width: "38px", margin: "0 1%", fontSize: "16px", borderRadius: "8px" }} onClick={() => setShowReadyRow(false)}>&times;</button>
                                        ) : (
                                            <button className="btn btn-danger" style={{ height: "38px", margin: "0 1%", fontSize: "15px", borderRadius: "8px" }} onClick={() => {
                                                const pairs = DAF_KEYS.map((key, idx) => ({ airflow: cyclone?.[key] ?? "0", note: cyclone?.[DAF_NOTE_KEYS[idx]] ?? "" }));
                                                pairs[i] = { airflow: "0", note: "" };
                                                const next = compactDafs(pairs);
                                                const patch = {};
                                                next.forEach((x, idx) => { patch[DAF_KEYS[idx]] = String(x.airflow ?? "0"); patch[DAF_NOTE_KEYS[idx]] = String(x.note ?? ""); });
                                                updateCyclone(patch);
                                            }}>&times;</button>
                                        )}

                                        <div style={{ width: "3%", margin: "1%", textAlign: "center", fontWeight: "bold" }}>{i + 1}</div>

                                        <div style={{ width: "14%", margin: "1%" }}>
                                            <input type="number" className="form-control" placeholder="0" value={isReadyRow ? "" : p.airflow}
                                                style={{ fontSize: "16px", textAlign: "right" }}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    updateCyclone({ [p.key]: v == "" ? "0" : v });
                                                    if (isReadyRow && Number(v) > 0) setShowReadyRow(false);
                                                }} />
                                        </div>

                                        <div style={{ width: "79%", margin: "1%" }}>
                                            <textarea rows="1" className="form-control" style={{ minWidth: "100%", fontSize: "16px" }} placeholder="Note" value={p.note} onChange={(e) => updateCyclone({ [p.noteKey]: e.target.value })} />
                                        </div>

                                    </div>
                                );
                            })}

                            {readyIndex < DAF_KEYS.length && !showReadyRow && (
                                <div style={{ width: "98%", margin: "1%", textAlign: "center" }}>
                                    <button className="btn btn-default no-print" style={{ fontSize: "15px", borderRadius: "4px", width: "66%" }} onClick={() => setShowReadyRow(true)}>+ Add Another Airflow</button>
                                </div>
                            )}

                            <div style={{ display: "flex", flexDirection: "row", gap: "12px", margin: "32px 1% 1% 1%", width: "98%", alignItems: "stretch" }}>
                                <div style={{ flex: "1", display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", border: "1px solid var(--bs-secondary)", borderRadius: "4px", padding: "8px 10px", backgroundColor: "var(--bs-light)" }}>
                                        <div style={{ color: "var(--bs-secondary)", whiteSpace: "nowrap" }}>Sub-Total <span style={{ color: "var(--bs-secondary)" }}>(CFM)</span></div>
                                        <div style={{ fontWeight: "bold" }}>{Number(dafSubTotal).toLocaleString("en-US", { minimumFractionDigits: 3 })}</div>
                                    </div>

                                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", border: "1px solid var(--bs-secondary)", borderRadius: "4px", padding: "8px 10px", backgroundColor: "var(--bs-light)" }}>
                                        <div style={{ color: "var(--bs-secondary)", whiteSpace: "nowrap" }}>Safety Factor (10%)</div>
                                        <div style={{ fontWeight: "bold" }}>+ {Number(dafSubTotal * 0.1).toLocaleString("en-US", { minimumFractionDigits: 3 })}</div>
                                    </div>
                                </div>

                                <div style={{ flex: "1.2", border: "2px solid #158cba", borderRadius: "4px", padding: "10px 12px", backgroundColor: "rgba(21, 140, 186, 0.08)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                    <div style={{ fontSize: "12px", letterSpacing: "0.5px", textTransform: "uppercase", color: "#158cba", fontWeight: "bold" }}>Total Adjusted Design Airflow</div>
                                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" }}>
                                        <div style={{ fontSize: "28px", fontWeight: "bold", color: "#158cba", lineHeight: "1" }}>{Number(dafTotal).toLocaleString("en-US", { minimumFractionDigits: 3 })}</div>
                                        <div style={{ fontSize: "14px", fontWeight: "bold", color: "#158cba" }}>CFM</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Cyclones */}
                    <div className="card no-print" style={{ width: "1200px", marginTop: "32px" }}>
                        <div className="card-header" style={{ backgroundColor: "#158cba", color: "var(--bs-light)" }}>
                            <span style={{ fontSize: "24px" }}>Cyclones</span>
                        </div>



                        <div className="card-body">
                            <table className="table table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center", verticalAlign: "middle" }}>Size</th>
                                        <th style={{ textAlign: "center", verticalAlign: "middle" }}>Type</th>
                                        <th style={{ textAlign: "center", verticalAlign: "middle" }}>Rated CFM</th>
                                        <th style={{ textAlign: "center", verticalAlign: "middle" }}>Actual SP</th>
                                        {/* <th style={{ textAlign: "center", verticalAlign: "middle" }}>Difference SP</th> */}
                                        {/* <th style={{ textAlign: "center", verticalAlign: "middle", width: "10%" }}>Recommended Size</th>   */}
                                        <th style={{ textAlign: "center", verticalAlign: "middle", width: "10%" }}>Selected Size</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!cyclone?.cycloneTypeSelection ? (
                                        <tr><td colSpan={7} style={{ textAlign: "center" }}><i>Select a Cyclone Type above</i></td></tr>
                                    ) : sizeModelsWithActualSP.length == 0 ? (
                                        <tr><td colSpan={7} style={{ textAlign: "center" }}><i>No size models found for this type</i></td></tr>
                                    ) : (
                                        sizeModelsWithActualSP.map(sm => {
                                            const isSelected = String(cyclone?.sizeModelID || "") !== "" && String(cyclone?.sizeModelID) == String(sm.id);

                                            const bg =
                                                isSelected ? "rgba(253, 126, 20, 0.25)"
                                                    : sm.Recommended ? "rgba(25, 135, 84, 0.05)"
                                                        : undefined;

                                            return (
                                                <tr key={sm.id} onClick={() => handleSelectSizeModel(sm)} style={{ cursor: "pointer", backgroundColor: bg }}>
                                                    <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg, fontWeight: isSelected ? "bold" : "normal" }}>{sm.Size}</td>
                                                    <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg }}>{sm.Type}</td>
                                                    <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg }}>{Number(sm.CFM || 0).toLocaleString("en-US")}</td>
                                                    <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg }}>{sm.ActualSP == "" ? <i>-</i> : sm.ActualSP}</td>
                                                    {/* <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg }}>{sm.DifferenceSP == "" ? <i>-</i> : sm.DifferenceSP}</td> */}
                                                    {/* <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg, fontSize: "18px" }}>{sm.Recommended ? "✓" : ""}</td>                                                                                         */}
                                                    <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: bg, fontSize: "18px" }}>{isSelected ? "✓" : ""}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                            {/* Color legend */}
                            <div className="card-body no-print" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "16px", marginBottom: "12px" }}>
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "6px" }}>
                                    <div style={{ width: "20px", height: "20px", backgroundColor: "rgba(253, 126, 20, 0.25)", border: "1px solid var(--bs-dark)" }}></div>
                                    <span>Selected Size Model</span>
                                </div>
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "6px" }}>
                                    <div style={{ width: "20px", height: "20px", backgroundColor: "rgba(25, 135, 84, 0.15)", border: "1px solid var(--bs-dark)" }}></div>
                                    <span>Recommended Size Model</span>
                                </div>
                            </div>

                            <div className="no-print" style={{ marginTop: "10px", color: "var(--bs-secondary)", fontSize: "14px" }}>
                                Click a row to select a size model.
                            </div>
                        </div>

                    </div>

                    {/* Selected Cyclone */}
                    <div className="card" style={{ width: "1200px", marginTop: "32px", breakBefore: "page", pageBreakBefore: "always" }}>
                        <div className="card-header" style={{ backgroundColor: "#158cba", color: "var(--bs-light)" }}>
                            <span style={{ fontSize: "24px" }}>Selected Cyclone Specs</span>
                        </div>

                        <div className="card-body">
                            {!selectedSizeModel ? (
                                <div style={{ textAlign: "center", color: "var(--bs-secondary)" }}><i>No size model selected.</i></div>
                            ) : (
                                <>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "12px" }}>

                                        </div>

                                        {isSelectedSameAsRecommended == false && (
                                            <div className="no-print" style={{ marginTop: "4px", border: "2px solid var(--bs-warning)", borderRadius: "6px", padding: "10px 12px", backgroundColor: "rgba(255, 193, 7, 0.15)" }}>
                                                <div style={{ fontWeight: "bold" }}>Note:</div>
                                                <div style={{ color: "var(--bs-secondary)" }}>This selected size model differs from the recommended result. <b>See Notes</b></div>
                                            </div>
                                        )}
                                    </div>

                                    {/* LEFT: data table, RIGHT: schematics */}
                                    <div style={{ display: "flex", flexDirection: "row", gap: "16px", marginTop: "12px", alignItems: "stretch" }}>

                                        {/* LEFT */}
                                        <div style={{ width: "30%", border: "1px solid var(--bs-secondary)", borderRadius: "6px", padding: "10px 12px", backgroundColor: "var(--bs-gray-200)" }}>
                                            <div style={{ color: "var(--bs-secondary)", fontSize: "12px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Size Model Dimensions</div>

                                            <table className="table table-bordered" style={{ margin: "10px 0px 0px 0px", backgroundColor: "var(--bs-white)" }}>
                                                <thead>

                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ fontWeight: "bold" }}>Size</td>
                                                        <td style={{ textAlign: "right" }}>{selectedSizeModel.Size}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ fontWeight: "bold" }}>Type</td>
                                                        <td style={{ textAlign: "right" }}>{selectedSizeModel.Type}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ fontWeight: "bold" }}>Rated CFM</td>
                                                        <td style={{ textAlign: "right" }}>{Number(selectedSizeModel.CFM || 0).toLocaleString("en-US")}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ fontWeight: "bold" }}>Actual SP</td>
                                                        <td style={{ textAlign: "right" }}>{selectedSizeModel.ActualSP == "" ? <i>-</i> : selectedSizeModel.ActualSP}</td>
                                                    </tr>
                                                    {[
                                                        ["Material Gauge", selectedSizeModel?.MaterialGauge],
                                                        ["Collector Weight", selectedSizeModel?.CollectorWeight],
                                                        ["CM Outlet Weight", selectedSizeModel?.CMOutletWeight],
                                                        ["Stand Weight", selectedSizeModel?.StandWeight],
                                                        ["AA", selectedSizeModel?.Dimension_AA],
                                                        ["A", selectedSizeModel?.Dimension_A],
                                                        ["B", selectedSizeModel?.Dimension_B],
                                                        ["C", selectedSizeModel?.Dimension_C],
                                                        ["D", selectedSizeModel?.Dimension_D],
                                                        ["E", selectedSizeModel?.Dimension_E],
                                                        ["G", selectedSizeModel?.Dimension_G],
                                                        ["H", selectedSizeModel?.Dimension_H],
                                                        ["K", selectedSizeModel?.Dimension_K],
                                                        ["L", selectedSizeModel?.Dimension_L],
                                                        ["M", selectedSizeModel?.Dimension_M],
                                                        ["P", selectedSizeModel?.Dimension_P],
                                                        ["Q_a", selectedSizeModel?.Dimension_Q_a],
                                                        ["Q_b", selectedSizeModel?.Dimension_Q_b],
                                                        ["Q_d", selectedSizeModel?.Dimension_Q_d],
                                                        ["R", selectedSizeModel?.Dimension_R],
                                                        ["S_Length", selectedSizeModel?.Dimension_S_Length],
                                                        ["S_Pcs", selectedSizeModel?.Dimension_S_Pcs],
                                                        ["S_Flange", selectedSizeModel?.Dimension_S_Flange],
                                                        ["S_Gauge", selectedSizeModel?.Dimension_S_Gauge],
                                                        ["T", selectedSizeModel?.Dimension_T],
                                                        ["X", selectedSizeModel?.Dimension_X],
                                                        ["Y", selectedSizeModel?.Dimension_Y],
                                                        ["Z", selectedSizeModel?.Dimension_Z],
                                                        ["ZZ", selectedSizeModel?.Dimension_ZZ],
                                                    ].map(([k, v]) => (
                                                        <tr key={k}>
                                                            <td style={{ fontWeight: "bold" }}>{k}</td>
                                                            <td style={{ textAlign: "right" }}>{(v == null || v == undefined || v == "") ? <i>-</i> : parseFloat(v).toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* RIGHT */}
                                        <div style={{ width: "70%", border: "1px solid var(--bs-secondary)", borderRadius: "6px", padding: "12px", backgroundColor: "var(--bs-light)" }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "center" }}>

                                                <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                    <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "#158cba" }}>Cyclone Side</label>
                                                    <img src="/img/Cyclones/Cyclone_Side.png" alt="Schematic" style={{ width: "100%", maxWidth: "720px" }} />
                                                </div>

                                                <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", breakBefore: "page", pageBreakBefore: "always" }}>
                                                    <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "#158cba" }}>Cyclone Top</label>
                                                    <img src="/img/Cyclones/Cyclone_Top.png" alt="Schematic" style={{ width: "100%", maxWidth: "720px" }} />
                                                </div>

                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", width: "100%", alignItems: "center" }}>
                                                    <label style={{ fontWeight: "bold", display: "block", marginBottom: "4px", color: "#158cba" }}>
                                                        Cyclone Discharge ({String(cyclone?.cycloneTypeSelection) == "P1" ? "Square" : "Round"})
                                                    </label>
                                                    <div style={{ position: "relative", display: "inline-block", width: "360px", textAlign: "center" }}>
                                                        <img
                                                            src={String(cyclone?.cycloneTypeSelection) == "P1" ? "/img/Cyclones/Cyclone_Discharge_Square.png" : "/img/Cyclones/Cyclone_Discharge_Round.png"}
                                                            alt="Cyclone Discharge"
                                                            style={{ width: "360px", height: "auto", borderRadius: "0.375rem", border: "1px solid #ced4da", padding: "4px", backgroundColor: "white" }}
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>

                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Feature Schematic */}
                    {cyclone?.capType != "1" &&
                        (
                            <>
                                <div className="card" style={{ width: "1200px", marginTop: "32px", breakBefore: "page", pageBreakBefore: "always" }}>
                                    <div className="card-header" style={{ backgroundColor: "#158cba", color: "var(--bs-light)" }}>
                                        <span style={{ fontSize: "24px" }}>Feature Specs</span>
                                    </div>

                                    <div className="card-body">
                                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                                            {/* TOP: schematic */}
                                            <div style={{ border: "1px solid var(--bs-secondary)", borderRadius: "6px", padding: "12px", backgroundColor: "var(--bs-light)" }}>
                                                <div style={{ fontWeight: "bold", color: "#158cba", marginBottom: "8px", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" }}>
                                                    <span>{selectedCapMeta.label} Schematic</span>
                                                    <span style={{ color: "var(--bs-secondary)", fontSize: "14px" }}>Cyclone / Cap Size: <b>{selectedSizeModel?.Size ?? "-"}</b></span>
                                                </div>

                                                <div style={{ width: "100%", height: "540px", display: "flex", justifyContent: "center", alignItems: "center", border: "1px dashed var(--bs-secondary)", borderRadius: "8px", backgroundColor: "var(--bs-white)" }}>
                                                    {cyclone?.capType != "1" ? (
                                                        <img src={selectedCapMeta.img} alt="Cap schematic" style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }} />
                                                    ) : (
                                                        <i style={{ color: "var(--bs-secondary)" }}>No schematic for this selection</i>
                                                    )}
                                                </div>
                                            </div>

                                            {/* BOTTOM: specs (two halves) */}
                                            <div style={{ border: "1px solid var(--bs-secondary)", borderRadius: "6px", padding: "10px 12px", backgroundColor: "var(--bs-gray-200)" }}>
                                                <div style={{ color: "var(--bs-secondary)", fontSize: "12px", letterSpacing: "0.5px", textTransform: "uppercase", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" }}>
                                                    <span>Outlet Cap Specs</span>
                                                    <span style={{ fontWeight: "bold", color: "#158cba" }}>{selectedCapMeta.label}</span>
                                                </div>

                                                {String(cyclone?.capType || "1") === "1" ? (
                                                    <div style={{ marginTop: "10px", color: "var(--bs-secondary)" }}><i>No cap selected.</i></div>
                                                ) : (
                                                    <div style={{ display: "flex", flexDirection: "row", gap: "12px", marginTop: "10px", alignItems: "stretch" }}>
                                                        <div style={{ width: "50%" }}>
                                                            <table className="table table-bordered" style={{ margin: 0, backgroundColor: "var(--bs-white)" }}>
                                                                <tbody>
                                                                    {capSpecsRows.slice(0, Math.ceil(capSpecsRows.length / 2)).map(([k, v]) => (
                                                                        <tr key={String(k)}>
                                                                            <td style={{ fontWeight: "bold" }}>{k}</td>
                                                                            <td style={{ textAlign: "right" }}>{(v === null || v === undefined || v === "") ? <i>-</i> : String(v)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        <div style={{ width: "50%" }}>
                                                            <table className="table table-bordered" style={{ margin: 0, backgroundColor: "var(--bs-white)" }}>
                                                                <tbody>
                                                                    {capSpecsRows.slice(Math.ceil(capSpecsRows.length / 2)).map(([k, v]) => (
                                                                        <tr key={String(k)}>
                                                                            <td style={{ fontWeight: "bold" }}>{k}</td>
                                                                            <td style={{ textAlign: "right" }}>{(v === null || v === undefined || v === "") ? <i>-</i> : String(v)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </div>


                                </div>
                            </>
                        )

                    }

                </div>
            </div>
        </>
    );
}

export default CyclonesDetail;
